import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendUrl, getImageUrl } from '../../lib/utils';
import { 
  Save, RefreshCw, CheckCircle2, AlertCircle, 
  UploadCloud, Megaphone, Trash2, HelpCircle, Eye, X
} from 'lucide-react';

const AdminPopup = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Announcement Popup State
  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [showType, setShowType] = useState('once');

  useEffect(() => {
    fetchPopupSettings();
  }, []);

  const fetchPopupSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/settings`);
      
      if (res.data.popup_settings) {
        const ps = res.data.popup_settings;
        setEnabled(!!ps.enabled);
        setTitle(ps.title || '');
        setContent(ps.content || '');
        setImageUrl(ps.image_url || '');
        setLinkUrl(ps.link_url || '');
        setLinkText(ps.link_text || '');
        setShowType(ps.show_type || 'once');
      }
    } catch (err) {
      console.error("Load popup settings failed", err);
      setError("Duyuru popup ayarları yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.post(`${backendUrl}/api/settings/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      setImageUrl(res.data.image_url);
      setSuccess("Popup görseli başarıyla yüklendi!");
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error("Upload failed", err);
      setError("Görsel yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');

    const popupData = {
      enabled,
      title: title.trim(),
      content: content.trim(),
      image_url: imageUrl,
      link_url: linkUrl.trim(),
      link_text: linkText.trim(),
      show_type: showType
    };

    try {
      const backendUrl = getBackendUrl();
      await axios.post(`${backendUrl}/api/settings`, {
        key_name: 'popup_settings',
        value_data: popupData
      }, { withCredentials: true });

      setSuccess("Duyuru popup ayarları başarıyla kaydedildi!");
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error("Save failed", err);
      setError("Ayarlar kaydedilemedi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs font-bold text-emerald-700 flex items-center gap-2 animate-fade-in shadow-sm shadow-emerald-50/50">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-xs font-bold text-rose-700 flex items-center gap-2 animate-fade-in shadow-sm shadow-rose-50/50">
          <AlertCircle size={16} className="text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div>
        <h2 className="text-xl font-extrabold text-slate-800 tracking-tight flex items-center gap-2">
          <Megaphone className="text-blue-600 animate-bounce" size={24} />
          Duyuru & Kampanya Pop-up Modülü
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Web sitenizi ziyaret eden kullanıcılara göstermek istediğiniz önemli duyuruları, haberleri veya kampanyaları içeren açılır pencereyi (pop-up) buradan yönetin.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Form Settings (Left / 7 cols) */}
        <form onSubmit={handleSaveSettings} className="lg:col-span-7 space-y-6">
          
          <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
                <Megaphone className="text-blue-500" size={18} />
                Pop-up Yapılandırması
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={enabled} 
                  onChange={(e) => setEnabled(e.target.checked)} 
                  className="sr-only peer" 
                />
                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                <span className="ml-2 text-xs font-bold text-slate-600">{enabled ? 'Gösteriliyor' : 'Gizli'}</span>
              </label>
            </div>

            {/* Popup Details */}
            <div className="space-y-4">
              {/* Title */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Duyuru Başlığı</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Örn: Önemli Şantiye Duyurusu!"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              {/* Content */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Duyuru İçerik Metni</label>
                <textarea
                  rows={4}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Duyurunun detaylarını açıklayan metin..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
                />
              </div>

              {/* Image Upload */}
              <div className="bg-slate-50 border border-slate-200/60 p-4 rounded-2xl space-y-3">
                <div className="space-y-0.5">
                  <h4 className="text-xs font-bold text-slate-800">Pop-up Görseli (Opsiyonel)</h4>
                  <p className="text-[10px] text-slate-500">Popup içinde görüntülenecek duyuru afişi veya görseli yükleyin.</p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold cursor-pointer transition shadow-md shadow-blue-600/10 shrink-0">
                    <UploadCloud size={14} /> Görsel Yükle
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  </label>

                  {imageUrl && (
                    <div className="flex items-center gap-3 border border-slate-200 p-2 bg-white rounded-xl">
                      <img src={getImageUrl(imageUrl)} alt="Preview" className="w-12 h-12 rounded object-cover" />
                      <button
                        type="button"
                        onClick={() => setImageUrl('')}
                        className="p-2 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg transition"
                        title="Görseli Kaldır"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Button Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    Buton Yönlendirme Linki (URL)
                  </label>
                  <input
                    type="text"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="Örn: /haberler/yeni-yonetim"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700">Buton Yazısı (Metin)</label>
                  <input
                    type="text"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    placeholder="Örn: Detayları Gör"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none"
                  />
                </div>
              </div>

              {/* Show Type */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Gösterim Sıklığı</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setShowType('once')}
                    className={`py-3 px-4 border rounded-xl text-xs font-bold transition-all ${
                      showType === 'once'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Her Ziyaretçiye 1 Kez (Önerilen)
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowType('always')}
                    className={`py-3 px-4 border rounded-xl text-xs font-bold transition-all ${
                      showType === 'always'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Her Sayfa Yenilendiğinde
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="text-right">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/10 disabled:opacity-50"
            >
              <Save size={16} />
              {loading ? "Kaydediliyor..." : "Popup Ayarlarını Kaydet"}
            </button>
          </div>
        </form>

        {/* Real-time Preview Simulator (Right / 5 cols) */}
        <div className="lg:col-span-5 space-y-6 lg:border-l lg:border-slate-100 lg:pl-8">
          <div>
            <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-1.5">
              <Eye size={16} className="text-slate-500" />
              Canlı Pop-up Önizlemesi
            </h3>
            <p className="text-[11px] text-slate-500 mt-1">Pop-up'ın web sitenizde ziyaretçilere nasıl görüneceğini gerçek zamanlı olarak buradan izleyin.</p>
          </div>

          {/* Web Popup Simulator Box */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 min-h-[380px] flex items-center justify-center relative overflow-hidden">
            {/* Background page emulator */}
            <div className="absolute inset-0 blur-sm opacity-20 pointer-events-none p-4 flex flex-col justify-between">
              <div className="w-1/2 h-4 bg-white rounded"></div>
              <div className="w-full h-24 bg-white rounded"></div>
              <div className="w-1/3 h-4 bg-white rounded"></div>
            </div>

            {/* Simulated Modal Box */}
            {enabled ? (
              <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden relative animate-fade-in z-20">
                {/* Header Image */}
                {imageUrl && (
                  <div className="h-32 w-full overflow-hidden bg-slate-100 relative">
                    <img src={getImageUrl(imageUrl)} alt="Popup Header" className="w-full h-full object-cover" />
                  </div>
                )}
                
                {/* Close X */}
                <div className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center cursor-pointer transition">
                  <X size={12} />
                </div>

                {/* Content Area */}
                <div className="p-5 space-y-3 text-center">
                  {title ? (
                    <h4 className="text-sm font-extrabold text-slate-900 leading-snug">{title}</h4>
                  ) : (
                    <h4 className="text-xs font-bold text-slate-300 italic">Başlık Belirtilmedi</h4>
                  )}

                  {content ? (
                    <p className="text-[11px] text-slate-600 leading-relaxed max-h-24 overflow-y-auto whitespace-pre-wrap">{content}</p>
                  ) : (
                    <p className="text-[10px] text-slate-300 italic">İçerik metni girilmedi.</p>
                  )}

                  {/* Button */}
                  {linkText && linkUrl && (
                    <div className="pt-2">
                      <button type="button" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold rounded-xl shadow-md transition w-full">
                        {linkText}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-slate-500 z-10">
                <Megaphone size={28} className="mx-auto text-slate-600 mb-2 opacity-50" />
                <p className="text-xs font-bold">Pop-up Şu Anda Devre Dışı</p>
                <p className="text-[10px] text-slate-600 mt-0.5">Önizlemek için sağ üstteki anahtarı aktif edin.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPopup;
